/**
 * Cliente de Metodologías (OT-0053 backend, OT-0055 interfaz).
 *
 * Backend: `app/api/v1/methodologies.py`. Una metodología agrupa el ciclo
 * de vida, los artefactos, roles y flujos de aprobación que la componen;
 * las plantillas sin metodología son genéricas ("Base" en la UI).
 *
 * Lectura: cualquier tier interno. Escritura: admin del tenant. Las del
 * producto (`tenant_id === null`) son de solo lectura.
 */

import { apiFetch } from './client'
import type { QualityReport, TemplateType } from './templates'
import type { SupportedLocale } from '../i18n/config'

export type MethodologyStatus = 'draft' | 'published' | 'archived'

export interface MethodologyI18nBlock {
  name: string
  description?: string | null
}

export type MethodologyI18n = Partial<Record<SupportedLocale, MethodologyI18nBlock>>

export interface Methodology {
  id: string
  /** `null` para las del producto; el tenant en otro caso. */
  tenant_id: string | null
  /** Va en las claves de sus plantillas (`rol-<code>-pm`). Inmutable. */
  code: string
  /** Resuelto al idioma del request (`Accept-Language`). */
  name: string
  description: string | null
  i18n: MethodologyI18n
  version: string
  status: MethodologyStatus
  /** La metodología por defecto del producto. */
  is_default: boolean
  /** La predeterminada de la organización: su ciclo se preselecciona al
   * crear proyectos. */
  is_tenant_default: boolean
  created_at: string
  updated_at: string
}

/** Códigos estables del reporte de completitud (contrato del backend). */
export type CompletenessIssueKind =
  | 'lifecycle_missing'
  | 'lifecycle_multiple'
  | 'reference_unresolved'
  | 'reference_foreign_methodology'
  | 'section_unresolved'
  | 'template_not_published'
  | 'definition_missing'

export interface CompletenessIssue {
  kind: CompletenessIssueKind
  template_key: string | null
  template_type: TemplateType | null
  /** Ruta dentro de la definición, p.ej. `phases[0].subphases[1]`. */
  location: string | null
  /** La clave o `plantilla.sección` que no resolvió. */
  reference: string | null
  expected_type: TemplateType | null
}

export type MemberOrigin = 'methodology' | 'generic'

export interface MethodologyMember {
  template_id: string
  template_key: string
  template_type: TemplateType
  status: string
  /** `methodology` (propia) o `generic` (compartida que usa). */
  origin: MemberOrigin
}

export interface CompletenessReport {
  methodology_id: string
  is_complete: boolean
  members: MethodologyMember[]
  issues: CompletenessIssue[]
}

export interface MethodologyTemplateRef {
  template_id: string
  template_key: string
  template_type: TemplateType
  methodology_id: string | null
}

export interface CreateMethodologyBody {
  /** `^[a-z][a-z0-9]{1,11}$` — ver `METHODOLOGY_CODE_PATTERN`. */
  code: string
  i18n: MethodologyI18n
  version?: string
}

export interface UpdateMethodologyBody {
  i18n?: MethodologyI18n
  version?: string
}

/** Espejo de `CODE_PATTERN` del backend (`app/schemas/methodology.py`). */
export const METHODOLOGY_CODE_PATTERN = /^[a-z][a-z0-9]{1,11}$/

export async function listMethodologies(
  accessToken: string,
  filters?: { status?: MethodologyStatus | undefined },
): Promise<Methodology[]> {
  const qs = filters?.status ? `?methodology_status=${filters.status}` : ''
  return apiFetch<Methodology[]>(`/api/v1/methodologies${qs}`, { accessToken })
}

export async function getMethodology(
  accessToken: string,
  methodologyId: string,
): Promise<Methodology> {
  return apiFetch<Methodology>(`/api/v1/methodologies/${methodologyId}`, {
    accessToken,
  })
}

export async function getMethodologyCompleteness(
  accessToken: string,
  methodologyId: string,
): Promise<CompletenessReport> {
  return apiFetch<CompletenessReport>(
    `/api/v1/methodologies/${methodologyId}/completeness`,
    { accessToken },
  )
}

export async function getMethodologyQuality(
  accessToken: string,
  methodologyId: string,
): Promise<QualityReport> {
  return apiFetch<QualityReport>(`/api/v1/methodologies/${methodologyId}/quality`, {
    accessToken,
  })
}

export async function createMethodology(
  accessToken: string,
  body: CreateMethodologyBody,
): Promise<Methodology> {
  return apiFetch<Methodology>('/api/v1/methodologies', {
    accessToken,
    method: 'POST',
    body,
  })
}

export async function updateMethodology(
  accessToken: string,
  methodologyId: string,
  body: UpdateMethodologyBody,
): Promise<Methodology> {
  return apiFetch<Methodology>(`/api/v1/methodologies/${methodologyId}`, {
    accessToken,
    method: 'PATCH',
    body,
  })
}

/** 422 `METHODOLOGY_INCOMPLETE` con el reporte en `details.report` si
 * falta algo. */
export async function publishMethodology(
  accessToken: string,
  methodologyId: string,
): Promise<Methodology> {
  return apiFetch<Methodology>(
    `/api/v1/methodologies/${methodologyId}/publish`,
    { accessToken, method: 'POST' },
  )
}

export async function archiveMethodology(
  accessToken: string,
  methodologyId: string,
): Promise<Methodology> {
  return apiFetch<Methodology>(
    `/api/v1/methodologies/${methodologyId}/archive`,
    { accessToken, method: 'POST' },
  )
}

/** 409 `METHODOLOGY_IN_USE` si todavía tiene plantillas. */
export async function deleteMethodology(
  accessToken: string,
  methodologyId: string,
): Promise<void> {
  return apiFetch<void>(`/api/v1/methodologies/${methodologyId}`, {
    accessToken,
    method: 'DELETE',
  })
}

/** `null` vuelve a la metodología por defecto del producto. */
export async function setTenantDefaultMethodology(
  accessToken: string,
  methodologyId: string | null,
): Promise<Methodology | null> {
  return apiFetch<Methodology | null>('/api/v1/methodologies/tenant-default', {
    accessToken,
    method: 'PUT',
    body: { methodology_id: methodologyId },
  })
}

/** Idempotente. Si la metodología estaba publicada vuelve a borrador. */
export async function assignTemplateToMethodology(
  accessToken: string,
  methodologyId: string,
  templateId: string,
): Promise<MethodologyTemplateRef> {
  return apiFetch<MethodologyTemplateRef>(
    `/api/v1/methodologies/${methodologyId}/templates/${templateId}`,
    { accessToken, method: 'PUT' },
  )
}

/** La plantilla vuelve a ser genérica. Un ciclo de vida no se puede quitar
 * (422 `METHODOLOGY_REQUIRED_FOR_LIFECYCLE`). */
export async function unassignTemplateFromMethodology(
  accessToken: string,
  methodologyId: string,
  templateId: string,
): Promise<MethodologyTemplateRef> {
  return apiFetch<MethodologyTemplateRef>(
    `/api/v1/methodologies/${methodologyId}/templates/${templateId}`,
    { accessToken, method: 'DELETE' },
  )
}
